import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';

import styles from './Login.module.scss';
import LogoMain from '../../assets/images/logo_siege_big.png';

import { request } from '../../shared/request';
import { ERRORS_MAP, ENV, WEBSITE_URL_HAVE_QUESTIONS } from '../../shared/constants';
import { withResponseHandling } from '../../shared/responseHandler';
import Loading from '../../components/Loading/Loading';
import PopupInfo from '../../components/PopupInfo/PopupInfo';
import {formatEventPassOffers, GA} from '../../shared/helpers';
import { initializeOffersFromApi } from '../../shared/offersHelpers';
import Footer from "../../components/Footer/Footer";
import ButtonNew from "../../components/buttons/ButtonNew/ButtonNew";

/** Maximum allowed length for player ID input */
const INPUT_REQUIRE_LENGTH = 10;

/** Default error type identifier */
const ERR_DEFAULT_TYPE = 1;

/** Error code for empty input validation */
const ERROR_EMPTY_INPUT = 4;

/**
 * Login Component - Handles player authentication via Player ID
 * 
 * This component provides a login interface where players can authenticate using their
 * unique player ID. It handles input validation, API authentication, error states,
 * and redirects authenticated users to the shop.
 * 
 * @component
 * @param {Object} props - Component properties
 * @param {Function} props.setPlayer - Callback to update player information in parent state
 * @param {Function} props.setIsLogged - Callback to update authentication status
 * @param {boolean} props.isLogged - Current authentication status
 * @param {Function} props.setOffers - Callback to update available offers
 * @param {Object} props.config - Configuration object containing UI text and error messages
 * @param {Object} props.windowSize - Current window dimensions
 * @param {Function} props.setIsMarketingEmailGiven - Callback to update marketing email consent status
 * @returns {JSX.Element} Login page component
 */
const Login = ({
    setPlayer,
    setIsLogged,
    isLogged,
    setOffers,
    config,
    windowSize,
    setIsMarketingEmailGiven
}) => {
    const navigate = useNavigate();
    
    /** Tracks if this is the first page view for analytics purposes */
    let isFirstPageView = useRef(true);
    
    const [searchParams] = useSearchParams();
    
    /** Current value of the player ID input field */
    const [inputValue, setInputValue] = useState('');
    
    /** Whether an error state is currently active */
    const [hasError, setHasError] = useState(false);
    
    /** Type/code of the current error for display mapping */
    const [errorType, setErrorType] = useState(ERR_DEFAULT_TYPE);
    
    /** Loading state during API authentication */
    const [isLoading, setIsLoading] = useState(false);
    
    /** Error object containing error state and optional message */
    /** Error object containing error state and optional message */
    const [error, setError] = useState({});

    /**
     * Handles input field changes with validation and sanitization
     * - Trims whitespace
     * - Limits length to INPUT_REQUIRE_LENGTH
     * - Removes all non-alphanumeric characters except '#'
     * 
     * @param {string} val - Raw input value from the text field
     */
    const onChange = (val) => {
        // remove symbols etc.
        let id = val.trim();
        if (INPUT_REQUIRE_LENGTH) {
            id = id.slice(0, INPUT_REQUIRE_LENGTH);
        }
        id = id.replace(/[^A-Za-z0-9#]+/g, '');
        // '719D57AA';
        setInputValue(id);
    };

    /**
     * Authenticates player by sending their ID to the backend API
     * 
     * Process flow:
     * 1. Validates input is not empty
     * 2. Sanitizes player ID (removes special characters except alphanumeric)
     * 3. Makes API request to get player info and shop data
     * 4. On success: stores player info, updates state, initializes offers, navigates to shop
     * 5. Tracks user analytics on first page view (production only)
     * 6. Handles errors and access denial appropriately
     * 
     * @async
     * @param {string} [playerId] - Optional player ID to use instead of input value
     */
    const sendId = async (playerId) => {
        let pId = playerId || inputValue;

        try {
            setIsLoading(true);
            
            // Validate that player ID is not empty
            if (!pId.length) {
                setHasError(true);
                setErrorType(ERRORS_MAP[ERROR_EMPTY_INPUT]);
                return;
            }
            
            // Remove all non-alphanumeric characters from the ID
            const idNoSymbols = pId.replace(/[^A-Za-z0-9]+/g, '');
            
            await withResponseHandling(
                () => request.get(`getWebShopInfo/${idNoSymbols}`),
                {
                    navigate,
                    onSuccess: (response) => {
                        // Create player info object from API response
                        const playerInfo = {
                            name: response.PlayerName,
                            id: pId.replace(/[^A-Za-z0-9]+/g, ''),
                            currency: response.Currency,
                        };
                        
                        // Persist player info to local storage for session management
                        localStorage.setItem('playerInfo', JSON.stringify(playerInfo));
                        setPlayer(playerInfo);
                        setIsLogged(true);
                        setIsMarketingEmailGiven(response.IsMarketingEmailGiven);
                        
                        // Use helper to initialize offers from API response
                        const offersData = initializeOffersFromApi(response, formatEventPassOffers);
                        setOffers(offersData);

                        navigate('/shop');

                        // Track user analytics only on first page view and only in production
                        // This prevents test environment data from polluting analytics
                        if (isFirstPageView.current && ENV === 'live') {
                        // if (isFirstPageView.current) {
                            // Determine user acquisition source from URL parameters
                            let userType = 'directWebShop';
                            const fromSite = searchParams.get('fromSite');
                            const fromGame = searchParams.get('fromGame');

                            if (fromSite) {
                                userType = 'siteToWebShop';
                            }

                            if (fromGame) {
                                userType = 'gameToWebShop';
                            }

                            // Set up Google Analytics tracking for this user
                            GA.setUserData(idNoSymbols);
                            GA.setUserDataProperties(idNoSymbols, userType);

                            GA.isUserTrackedAsLogged = true;
                            isFirstPageView.current = false;
                        }
                    },
                    onAccessDenied: (reason) => {
                        // Handle access denied scenarios (e.g., banned user, invalid ID)
                        setHasError(true);
                        setErrorType(ERRORS_MAP[reason]);
                    },
                    onError: (err) => {
                        // Generic error handler - shows detailed error in test environment only
                        setError({
                            hasError: true,
                            message: ENV === 'test' ? err.message : null,
                        });
                    },
                }
            );
        } catch (err) {
            // Catch-all error handler for unexpected errors
            setError({
                hasError: true,
                message: ENV === 'test' ? err.message : null,
            });
        } finally {
            // Always hide loading state regardless of success/failure
            setIsLoading(false);
        }
    };

    /**
     * Handles Enter key press in input field to submit the form
     * Triggers authentication when user presses Enter (keyCode 13)
     * 
     * @param {KeyboardEvent} event - Keyboard event object
     */
    const handleKeyDown = (event) => {
        if (event.keyCode === 13) {
            sendId(event.target.value);
        }
    };

    /**
     * Clears error state when user focuses on the input field
     * Provides better UX by removing error message when user starts typing
     */
    const setInputFocus = () => {
        if (hasError) {
            setHasError(false);
        }
    };

    /**
     * Effect hook that handles player authentication and navigation logic.
     * Redirects authenticated users to the shop if they have valid player data.
     *
     * @effect
     * @dependencies {boolean} isLogged - Current authentication status of the user
     * @dependencies {URLSearchParams} searchParams - URL search parameters
     */
    useEffect(() => {
        // Retrieve cached player information from browser's local storage
        let player = localStorage.getItem('playerInfo');

        // Parse JSON strings back to JavaScript objects (null if item doesn't exist)
        player = JSON.parse(player);

        // Read the player id from the URL (used for direct "/?pid=..." entry links)
        const pid = searchParams.get('pid');

        // Direct-entry scenario: if a player ID is present in the URL and either the
        // user isn't logged in OR it's a different player, (re)authenticate that id.
        // (The `pid !== player.id` check restores the master fix for switching players.)
        if (pid && (!isLogged || pid !== player?.id)) {
            sendId(pid);
        }
        // Authenticated scenario: redirect to shop when logged in or valid cached player data exists.
        else if (isLogged || (player && player.name && player.id)) {
            // Navigate to shop page since user is authenticated
            navigate('/shop');
        }
    }, [isLogged, searchParams]); // Re-run effect when authentication status or URL parameters change

    /**
     * Effect hook to set up and clean up keyboard event listener
     * Enables Enter key submission for the login form
     * 
     * @effect
     * @cleanup Removes event listener on component unmount
     */
    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    // Render loading screen during authentication
    return isLoading ? (
        <Loading />
    ) : (
        <>
            <div className={styles.loginWrapper}>
                {/* Info text section (currently empty) */}
                <div className={styles.infoText}>{/*{config.loginInfo}*/}</div>
                
                <div className={styles.ctaWrapper}>
                    {/* Main logo */}
                    <img className={styles.logo} src={LogoMain} alt={''} />
                    
                    {/* Login instruction text */}
                    <div className={styles.enterIdText}>{config.signWithId}</div>
                    
                    {/* Player ID input field */}
                    <div className={styles.inputWrapper}>
                        <input
                            placeholder={config.idPlaceHolder}
                            onFocus={setInputFocus}
                            onChange={(e) => {
                                const val = e.target.value;
                                onChange(val);
                            }}
                            value={inputValue}
                        />
                    </div>
                    
                    {/* Error message display */}
                    {hasError ? (
                        <div className={styles.errMessage}>
                            {config.errors[errorType]}
                        </div>
                    ) : null}
                    
                    {/* Login submit button */}
                    <ButtonNew isFancy={true} isLarge={true} text={config.login} onClick={sendId} />
                </div>
                
                {/* Help links section */}
                <div className={styles.linksWrapper}>
                    <NavLink to={'find-id'} className={styles.troubleShoot}>
                        {config.problemFindingId}
                    </NavLink>
                    <div>
                        <a className={styles.troubleShoot} href={WEBSITE_URL_HAVE_QUESTIONS}>{config.havingQuestions}</a>
                    </div>
                </div>
                
                {/* Generic error popup for unexpected errors */}
                <AnimatePresence>
                    {error.hasError ? (
                        <PopupInfo text={error.message} setError={setError} />
                    ) : null}
                </AnimatePresence>
                
                {/* Footer component */}
                <Footer config={config} windowSize={windowSize} />
            </div>
        </>
    );
};

export default Login;
